import { useEffect, useMemo } from 'react';
import get from 'lodash/get';
import { Field, useFormikContext } from 'formik';
import { DateTimeInput as SvpDateTimeInput } from '@schibsted-svp/react-ui';

import { FormField } from '../shared/FormField/FormField';

import type { FC } from 'react';
import type { FieldProps } from 'formik';

import './DateTimeInput.module.scss';

function isUnixTimestamp(time: number) {
  return time && time.toString().length === 10;
}

function dateToUnixTimestamp(date: Date) {
  return Math.round(date.getTime() / 1000);
}

type Props = {
  label: string;
  name: string;
  startDate?: Date;
  onClear?: () => void;
  [key: string]: unknown;
};

const DateTimeInput: FC<Props> = ({
  label,
  name,
  startDate,
  onClear,
  ...props
}) => {
  const { setFieldValue, values, touched } = useFormikContext();

  const isTouched = useMemo(
    () => (touched as Record<string, unknown>)[name],
    [touched, name]
  );

  // if the field is empty, but a startDate is set - use it as the initial value
  const value = useMemo(() => {
    const formValue = get(values, name);

    // we need to both set the startDate here visually and in the useEffect below to make it work
    if (!formValue && startDate && !isTouched) {
      return startDate;
    }

    return formValue;
  }, [isTouched, name, startDate, values]);

  useEffect(() => {
    const formValue = get(values, name);

    if (!formValue && startDate && !isTouched) {
      setFieldValue(name, dateToUnixTimestamp(startDate));
    }
  }, [isTouched, name, setFieldValue, startDate, values]);

  const date = useMemo(
    () =>
      value
        ? new Date(isUnixTimestamp(value) ? value * 1000 : value)
        : undefined,
    [value]
  );

  function onChange(date?: Date) {
    const seconds = date ? dateToUnixTimestamp(date) : undefined;

    setFieldValue(name, seconds);

    if (!seconds && typeof onClear === 'function') {
      onClear();
    }
  }

  return (
    <FormField label={label}>
      <Field {...props}>
        {({ field, meta }: FieldProps) => (
          <SvpDateTimeInput
            {...field}
            {...props}
            name={name}
            value={date}
            error={typeof meta.error === 'string' ? meta.error : undefined}
            onChange={onChange}
            datePickerProps={{
              disabledKeyboardNavigation: true,
              todayButton: 'Today',
              dateFormat: 'dd.MM.yyyy HH:mm',
              timeIntervals: 5,
              showTimeSelect: true,
              minDate: startDate,
              openToDate: startDate,
            }}
          />
        )}
      </Field>
    </FormField>
  );
};

export { DateTimeInput };
